import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBackState, useScrollMemory } from "@/hooks/useScrollMemory";
import { RecommendedRow } from "@/components/sales/RecommendedRow";
import { NightLine } from "@/components/sales/NightLine";
import { UndoBar, useUndoBar } from "@/components/sales/UndoBar";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

// RECOMMENDED TO SELL (C3: on top). S2.2: the list worked out every night, each part with its photo and its reasons in
// plain words; one chip per reason with its count (tap to filter, tap again for all); Prepare · Snooze (30 / 90 days /
// Not this one) · Keep for the shop; every action on the Undo bar.
export function RecommendedTab({ caps, onChanged }) {
  const [page, setPage] = useBackState("rec-page", 1);
  const [reason, setReason] = useBackState("rec-reason", "");
  const [res, setRes] = useState(null);
  const bar = useUndoBar();
  const load = () => api.get("/sales/recommended", { params: { page, page_size: 50, reason: reason || undefined } })
    .then((r) => setRes(r.data)).catch(() => setRes({ error: true }));
  useEffect(() => { load(); }, [page, reason]); // eslint-disable-line react-hooks/exhaustive-deps
  useScrollMemory(res != null);
  const after = () => { load(); onChanged?.(); };
  if (!res) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  if (res.error) return (
    <div data-testid="sales-recommended-error" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 flex flex-wrap items-center gap-3">
      <span className="flex-1 min-w-[200px]"><b>Couldn't read Recommended to sell.</b> Nothing was changed — try again.</span>
      <button type="button" data-testid="sales-recommended-retry" onClick={() => { setRes(null); load(); }}
        className="h-11 px-4 rounded-lg border-2 border-amber-700 bg-white text-amber-900 text-sm font-bold hover:bg-amber-100">Try again</button>
    </div>
  );
  const pick = (k) => { setReason(reason === k ? "" : k); setPage(1); };
  return (
    <div data-testid="sales-recommended" className="space-y-3">
      <NightLine night={res.night} canWorkOut={!!caps.set_sales_rules} onDone={load} />
      <ReasonChips res={res} reason={reason} pick={pick} />
      <p data-testid="sales-recommended-total" className="text-sm text-slate-600"><b className="text-slate-900">{res.total}</b> part{res.total === 1 ? "" : "s"} recommended to sell{reason ? " for this reason" : ""}</p>
      {!res.rows.length && <p data-testid="sales-recommended-empty" className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Nothing is recommended right now. Only complete parts get the nightly reasons; a part flagged to sell shows here until someone prepares it or keeps it for the shop.</p>}
      {res.rows.map((r) => <RecommendedRow key={r.id} r={r} caps={caps} after={after} bar={bar} />)}
      <Pager page={page} setPage={setPage} total={res.total} size={50} testid="sales-recommended" />
      <UndoBar bar={bar} after={after} />
    </div>
  );
}

function ReasonChips({ res, reason, pick }) {
  if (!res.chips?.length) return null;
  const chip = (k, words, n) => (
    <button key={k || "all"} type="button" data-testid={`sales-reason-${k || "all"}`} onClick={() => pick(k)} aria-pressed={reason === k}
      className={`h-11 px-3 rounded-full border text-sm font-semibold ${reason === k ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
      {words} <span className="font-mono opacity-70">{n}</span>
    </button>
  );
  return (
    <div data-testid="sales-reason-chips" className="flex flex-wrap gap-1.5">
      {chip("", "All", res.all)}
      {res.chips.map((c) => chip(c.key, c.words, c.n))}
    </div>
  );
}

export function Pager({ page, setPage, total, size, testid }) {
  const last = Math.max(1, Math.ceil(total / size));
  if (last <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 pt-2">
      <button type="button" data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
      <span data-testid={`${testid}-page`} className="text-sm font-mono">{page} / {last}</span>
      <button type="button" data-testid={`${testid}-next`} disabled={page >= last} onClick={() => setPage(page + 1)} className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}
